import { object, string } from 'yup'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import {
  Alert,
  Button,
  Grid,
  InputLabel,
  MenuItem,
  TextField,
} from '@mui/material'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  isPresent,
  applyPhoneMask,
  getPhoneMaskRegex,
  removePhoneMask,
} from '@jjvgaming/player-payback-library'
import { isNil } from 'lodash'
import { ActivityButton } from 'components/ActivityButton'
import { type EditLicensedEstablishmentDetailsProps } from '../types'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { Modal } from 'components/Modal/Modal'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { useLayout } from 'hooks/useLayout'
import { type LicensedEstablishmentContactType } from 'types/api'
import { useSetLEContactId } from 'stores/useSetLEContactId'
import { useEffect } from 'react'

const LicensedEstablishmentContactDetailsSchema = object({
  Contact: object({
    contact: string()
      .test(
        'two-words',
        'Contact must be First Name and Last Name',
        (value) => {
          if (!isNil(value)) {
            return /^\w+\s+\w+$/.test(value)
          }
          return false
        }
      )
      .required('Contact is a required field'),
    email: string().email().required('Email is a required field'),
    contactType: string<LicensedEstablishmentContactType>().required(
      'Contact Type is a required field'
    ),
    phoneNumber: string()
      .matches(getPhoneMaskRegex, {
        message: 'Please enter a valid phone number',
      })
      .required('Phone number is a required field'),
  }),
})

export const EditLicensedEstablishmentContactDetails = ({
  licensedEstablishment,
  updateIsActive,
  isModalOpen,
  toggleModalIsOpen,
  onSubmitLicensedEstablishment,
}: EditLicensedEstablishmentDetailsProps) => {
  const { isMobile } = useLayout()
  const heightModal = isMobile ? '100%' : '75%'
  const { passedLEContactId } = useSetLEContactId()
  const formMethods = useForm({
    resolver: yupResolver(LicensedEstablishmentContactDetailsSchema),
  })

  const leContact = licensedEstablishment.contacts?.filter(
    (x) => x.id === passedLEContactId
  )[0]

  const {
    control,
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    if (!isModalOpen) {
      reset() // reset form when modal closes
    }
  }, [isModalOpen, reset])

  const handleSubmitWrapper = handleSubmit((formData) => {
    const contactData = formData.Contact

    onSubmitLicensedEstablishment({
      ...licensedEstablishment,
      contacts: [
        {
          id: Number(passedLEContactId ?? 0),
          firstName: contactData.contact.split(' ')[0],
          lastName: contactData.contact.split(' ')[1],
          email: contactData.email,
          phoneNumber: removePhoneMask(contactData.phoneNumber),
          contactType: contactData.contactType,
        },
      ],
    })
  })

  return (
    <Modal
      isOpen={isModalOpen}
      toggleIsOpen={() => {}}
      sx={{
        width: isMobile ? '100%' : '55%',
        height: heightModal,
      }}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmitWrapper} noValidate>
          <ModalFullHeader
            toggleIsOpen={toggleModalIsOpen}
            title="Contact"
            subTitle={`Licensed Establishment "${licensedEstablishment.standardName}"`}
          />
          <ModalFullBody heightModal={heightModal}>
            {errors.Contact && (
              <Grid item xs={12} pb={4}>
                <Alert severity="error">
                  Please correct errors to continue
                </Alert>
              </Grid>
            )}

            <Grid container>
              <Grid item xs={12} md={6} pt={{ md: 2 }}>
                <InputLabel
                  required
                  htmlFor="contactType"
                  sx={{ fontSize: '16px', fontWeight: '100' }}
                >
                  Type
                </InputLabel>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  {...register('Contact.contactType', {
                    required: true,
                  })}
                  defaultValue={leContact?.contactType}
                  required
                  fullWidth
                  select
                >
                  <MenuItem
                    value="LocationSweepstakes"
                    disabled={
                      leContact?.contactType !== undefined &&
                      leContact?.contactType !== 'LocationSweepstakes'
                    }
                  >
                    Location Sweepstakes
                  </MenuItem>
                </TextField>
                {errors.Contact?.contactType?.message && (
                  <FormFieldErrorMessage
                    message={errors.Contact?.contactType.message}
                  />
                )}
              </Grid>
            </Grid>

            <Grid container pt={4}>
              <Grid item xs={12} md={6} pt={{ md: 2 }}>
                <InputLabel
                  required
                  htmlFor="contact"
                  sx={{ fontSize: '16px', fontWeight: '100' }}
                >
                  Contact
                </InputLabel>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  {...register('Contact.contact', { required: true })}
                  defaultValue={
                    leContact
                      ? `${leContact?.firstName} ${leContact?.lastName}`
                      : ''
                  }
                  error={isPresent(errors.Contact?.contact)}
                />
                {errors.Contact?.contact?.message && (
                  <FormFieldErrorMessage
                    message={errors.Contact?.contact.message}
                  />
                )}
              </Grid>
            </Grid>

            <Grid container pt={4}>
              <Grid item xs={12} md={6} pt={{ md: 2 }}>
                <InputLabel
                  required
                  htmlFor="email"
                  sx={{ fontSize: '16px', fontWeight: '100' }}
                >
                  Email
                </InputLabel>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  {...register('Contact.email', { required: true })}
                  defaultValue={leContact ? leContact?.email : ''}
                  error={isPresent(errors.Contact?.email)}
                />
                {errors.Contact?.email?.message && (
                  <FormFieldErrorMessage
                    message={errors.Contact?.email.message}
                  />
                )}
              </Grid>
            </Grid>

            <Grid container pt={4}>
              <Grid item xs={12} md={6} pt={{ md: 2 }}>
                <InputLabel
                  required
                  htmlFor="Contact.phoneNumber"
                  sx={{ fontSize: '16px', fontWeight: '100' }}
                >
                  Phone Number
                </InputLabel>
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="Contact.phoneNumber"
                  control={control}
                  defaultValue={
                    leContact
                      ? applyPhoneMask(String(leContact?.phoneNumber))
                      : ''
                  }
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TextField
                      fullWidth
                      error={isPresent(errors.Contact?.phoneNumber)}
                      onChange={(phoneNumber) => {
                        onChange(
                          applyPhoneMask(String(phoneNumber.target.value))
                        )
                      }}
                      onBlur={onBlur}
                      value={value}
                    />
                  )}
                />
                {errors.Contact?.phoneNumber?.message && (
                  <FormFieldErrorMessage
                    message={errors.Contact?.phoneNumber.message}
                  />
                )}
              </Grid>
            </Grid>
          </ModalFullBody>
          <ModalFullFooter>
            <Button variant="text" onClick={toggleModalIsOpen}>
              Cancel
            </Button>
            <ActivityButton
              active={updateIsActive}
              type="submit"
              disabled={!!errors.Contact}
              variant="contained"
            >
              Save
            </ActivityButton>
          </ModalFullFooter>
        </form>
      </FormProvider>
    </Modal>
  )
}
